import { ArrowUpRight, Quote } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { IMG, CALENDLY_URL } from "@/data/content";

const FAMILIAR = [
  "You are relied on heavily, but not fully recognised.",
  "You consistently deliver, but remain outside the conversations that shape decisions.",
  "You are seen as capable, but not yet perceived as leadership material.",
  "You feel over-utilised operationally and under-leveraged strategically.",
  "You work harder and harder, yet your progression no longer matches your contribution.",
];

const PILLARS = [
  {
    n: "01",
    title: "From execution to leadership presence.",
    body: "Stop being the engine behind everything. Start becoming the person shaping direction, influence, and decisions.",
  },
  {
    n: "02",
    title: "Visibility and strategic influence.",
    body: "Move from reporting on decisions to influencing the conversations that shape them.",
  },
  {
    n: "03",
    title: "Positioning yourself for progression.",
    body: "Learn how to build visibility, credibility, and strategic leverage before the opportunity appears.",
  },
  {
    n: "04",
    title: "Stakeholders and organisational dynamics.",
    body: "Navigate politics, influence senior stakeholders, and operate with greater strategic composure.",
  },
  {
    n: "05",
    title: "Authority without overcompensating.",
    body: "Develop a leadership style that is influential, credible, and fully your own, without performing or shrinking.",
  },
];

const TESTIMONIALS = [
  {
    pull: "Moved from senior individual contributor to Global Head within 18 months, with a 30% compensation increase.",
    author: "E.",
    role: "Senior Finance Lawyer",
  },
  {
    pull: "Negotiated a 30% salary increase, a fully funded Master's degree, and additional study leave within 12 months.",
    author: "Helena",
    role: "Head of Legal",
  },
  {
    pull: "Transitioned from a family business environment into project management and was promoted two levels into a managerial role within 18 months.",
    author: "Andreea C.",
    role: "Project Manager",
  },
];

export default function Coaching() {
  return (
    <div data-testid="coaching-page">
      {/* HERO */}
      <section className="pt-36 lg:pt-44 pb-20 lg:pb-28 bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />1-2-1 Leadership Coaching</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="gwa-display text-[2.4rem] sm:text-5xl lg:text-[4.5rem] mt-6 text-gwa-primary leading-[1.04]">
                You are delivering at a high level.<br />
                You are not being seen as a <em className="italic font-light text-gwa-purple">leader.</em>
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-9 space-y-5 text-lg lg:text-xl text-gwa-muted-fg max-w-2xl leading-relaxed">
                <p>
                  You are already trusted to deliver. The problem is that senior progression is no longer driven by delivery alone.
                </p>
                <p>
                  At senior level, visibility, influence, positioning, and leadership presence start to matter as much as competence.
                </p>
                <p>
                  I work with high-performing women who are ready to stop operating as exceptional executors and start operating as influential leaders.
                </p>
              </div>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-10">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="coaching-hero-cta"
                  className="gwa-btn-primary"
                >
                  Book a conversation <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={300}>
              <div className="relative aspect-[4/5] overflow-hidden bg-gwa-secondary">
                <img
                  src={IMG.navyAlt}
                  alt="Pascale Lebaillif"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="coaching-hero-image"
                />
                <span className="absolute -bottom-2 -left-2 w-24 h-px bg-gwa-purple" />
                <span className="absolute -top-2 -right-2 w-24 h-px bg-gwa-gold" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* IF THIS FEELS FAMILIAR */}
      <section className="bg-gwa-ivory border-y border-gwa-border">
        <div className="gwa-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />If this feels familiar</p>
              <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-[1.05]">
                You are operating below<br />your actual level.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-gwa-border border-y border-gwa-border">
              {FAMILIAR.map((line, i) => (
                <Reveal key={line} delay={60 * i} as="li" className="py-5 flex items-start gap-5">
                  <span className="font-serif text-gwa-purple text-xl leading-none mt-1 w-6 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-lg text-gwa-primary leading-snug">{line}</p>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CORE INSIGHT */}
      <section className="bg-[#651A75] text-gwa-bg gwa-noise" data-testid="coaching-insight">
        <div className="gwa-container py-24 lg:py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <Reveal>
                <p className="gwa-eyebrow-light"><span className="gwa-rule" />Core insight</p>
              </Reveal>
            </div>
            <div className="lg:col-span-9">
              <Reveal delay={120}>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.06]">
                  This is not a <em className="italic font-light text-gwa-gold">capability</em> problem.
                </h2>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-8 space-y-5 text-lg lg:text-xl text-gwa-bg/85 leading-[1.7] font-light max-w-3xl">
                  <p>
                    At senior level, progression is no longer determined solely by the quality of your work.
                  </p>
                  <p>
                    It is shaped by how you are perceived, how you position yourself, how you influence stakeholders, and how you operate inside complex organisational systems.
                  </p>
                  <p>
                    Most high-performing women are never taught this transition. They simply continue doing more of what made them successful earlier in their careers.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={360}>
                <div className="mt-10 pt-8 border-t border-white/15 space-y-1.5">
                  <p className="font-serif italic text-gwa-gold text-2xl lg:text-[1.85rem] leading-snug">
                    Leadership is not more execution.
                  </p>
                  <p className="font-serif italic text-gwa-gold text-2xl lg:text-[1.85rem] leading-snug">
                    It is influence, visibility, positioning, and strategic authority.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* HOW I WORK */}
      <section className="bg-gwa-secondary">
        <div className="gwa-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />How I work</p>
              <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight">
                Focused.<br />Strategic.<br />One-to-one.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-4 text-lg text-gwa-primary leading-relaxed">
            {[
              "Tailored to your specific role, ambitions, and organisational context.",
              "Grounded in the realities of senior corporate environments and power dynamics.",
              "Direct, practical, psychologically informed, and outcome-oriented.",
            ].map((line, i) => (
              <Reveal key={line} delay={60 * i}>
                <div className="flex items-start gap-4 py-3 border-b border-gwa-border">
                  <span className="text-gwa-purple font-serif text-xl leading-none mt-1">•</span>
                  <p>{line}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={240}>
              <p className="font-serif italic text-2xl text-gwa-purple pt-4 leading-snug">
                You will notice shifts not only in how you are perceived, but in how you think, operate, influence, and position yourself within months.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FIVE SHIFTS */}
      <section className="gwa-section bg-gwa-bg">
        <div className="gwa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />What we work on</p>
                <h2 className="gwa-display text-4xl lg:text-[3rem] mt-6 text-gwa-primary leading-[1.05]">
                  Five shifts.<br />Practical. Strategic. Measurable.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-px bg-gwa-border">
            {PILLARS.map((p, i) => {
              const span = i < 2 ? "lg:col-span-6" : "lg:col-span-4";
              return (
                <Reveal
                  key={p.n}
                  delay={70 * i}
                  className={`bg-gwa-bg p-8 lg:p-10 flex flex-col gap-4 ${span}`}
                >
                  <p className="font-serif text-4xl text-gwa-purple leading-none">{p.n}</p>
                  <h3 className="font-serif text-2xl text-gwa-primary leading-snug">{p.title}</h3>
                  <p className="text-gwa-muted-fg leading-relaxed text-[0.95rem]">{p.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CREDIBILITY */}
      <section className="gwa-section bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="aspect-[4/5] overflow-hidden bg-gwa-secondary">
                <img src={IMG.navy} alt="Pascale Lebaillif" className="w-full h-full object-cover" data-testid="coaching-credibility-image" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Credibility</p>
              <h2 className="gwa-display text-4xl lg:text-[3rem] mt-6 text-gwa-primary leading-[1.05]">
                Twenty years in the rooms where careers are decided.
              </h2>
              <p className="mt-8 text-lg text-gwa-muted-fg leading-[1.7] max-w-2xl">
                Twenty years in senior roles at Morgan Stanley, Credit Suisse, and J.P. Morgan. I have seen what actually drives progression, and what quietly holds people back.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OUTCOMES — testimonials */}
      <section className="bg-gwa-ivory border-y border-gwa-border">
        <div className="gwa-container py-20 lg:py-28">
          <Reveal>
            <p className="gwa-eyebrow"><span className="gwa-rule" />Outcomes</p>
            <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary max-w-3xl">
              The shift is measurable.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-gwa-border">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.author} delay={80 * i} className="bg-gwa-ivory p-8 lg:p-10 flex flex-col">
                <Quote className="w-5 h-5 text-gwa-accent mb-5" />
                <p className="font-serif italic text-xl lg:text-2xl text-gwa-primary leading-snug flex-grow">
                  “{t.pull}”
                </p>
                <div className="mt-7">
                  <p className="font-medium text-gwa-primary">{t.author}</p>
                  <p className="text-sm text-gwa-muted-fg tracking-wide">{t.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gwa-bg">
        <div className="gwa-container py-24 lg:py-32 text-center">
          <Reveal>
            <p className="gwa-eyebrow justify-center"><span className="gwa-rule" />Engage</p>
            <h2 className="gwa-display text-4xl sm:text-5xl lg:text-6xl mt-7 text-gwa-primary max-w-3xl mx-auto leading-[1.05]">
              If you recognise yourself in this, we should talk.
            </h2>
            <div className="mt-12">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="coaching-closing-cta"
                className="gwa-btn-primary"
              >
                Book a conversation <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
